import { type NetworkCallbacks } from './caching/persist-refresh';
import { extractTraceInfoFromResponse } from './network-trace-info';

/**
 * Wraps an async function with VitalStats network callbacks.
 * 
 * Automatically extracts trace info (request_id, trace_id) from GraphQL
 * responses that contain extensions.gateway fields.
 * 
 * @param fn - The async function to wrap
 * @param callbacks - Optional network callbacks for VitalStats tracking
 * @returns A wrapped function that fires VitalStats events
 * 
 * @example
 * ```typescript
 * const wrappedFetch = withNetworkCallback(
 *   () => fetchData(),
 *   networkCallbacks
 * );
 * ```
 */
export const withNetworkCallback = <T>(
	fn: () => Promise<T>,
	callbacks?: NetworkCallbacks,
): (() => Promise<T>) => {
	if (!callbacks) {
		return fn;
	}

	return async () => {
		callbacks.onStart();
		try {
			const result = await fn();
			const traceInfo = extractTraceInfoFromResponse(result);
			callbacks.onSuccess(traceInfo);
			return result;
		} catch (error: any) {
			const errorName = error?.name || 'UnknownError';
			const errorMessage = error?.message || 'An unknown error occurred';
			callbacks.onError(errorName, errorMessage);
			throw error;
		}
	};
};
